/**
 * Zentrale Konstanten (Single Source of Truth)
 *
 * Diese Datei ist die EINZIGE Quelle für Spielkonstanten.
 * Server-Werte sind autoritativ für Spiellogik.
 *
 * Verwendung:
 * - Server: const { BASE_POINTS, TRIGGER } = require('../../src/shared/core/constants');
 * - Client: import { BASE_POINTS, TRIGGER } from './shared/core/constants';
 *
 * MIGRATION: Diese Datei ersetzt die duplizierten Dateien:
 * - src/core/constants.js (deprecated, re-exports von hier)
 * - server/core/constants.js (deprecated, re-exports von hier)
 */

// =============================================================================
// RARITÄTEN
// =============================================================================

const RARITY = {
  COMMON: 'common',
  UNCOMMON: 'uncommon',
  RARE: 'rare',
  LEGENDARY: 'legendary'
};

// =============================================================================
// EFFEKT-KATEGORIEN
// =============================================================================

const EFFECT_CATEGORY = {
  ECONOMY: 'economy',     // Coins, money
  TEMPO: 'tempo',         // Extra plays, draws
  POWER: 'power',         // Card strength modifications
  SCORING: 'scoring',     // Points and mult
  DEFENSIVE: 'defensive'  // Protection effects
};

// =============================================================================
// TRIGGER (Auslösezeitpunkte für Effekte)
// =============================================================================

const TRIGGER = {
  // Card play triggers
  ON_PLAY: 'onPlay',              // When cards are played
  ON_WIN_TRICK: 'onWinTrick',     // When a trick is won
  ON_LOSE_TRICK: 'onLoseTrick',   // When a trick is lost
  ON_PASS: 'onPass',              // When player passes
  ON_DRAW: 'onDraw',              // When drawing a card
  ON_CARD_RECEIVED: 'onCardReceived', // When any card is added to hand (draw, exchange, effects)

  // Round triggers
  ON_ROUND_START: 'onRoundStart', // At beginning of round
  ON_ROUND_END: 'onRoundEnd',     // At end of round
  ON_PARTIE_END: 'onPartieEnd',   // At end of partie (game mode)
  ON_FINISH_FIRST: 'onFinishFirst', // When first player finishes
  ON_FINISH_LAST: 'onFinishLast',   // When last player finishes
  ON_PLAYER_FINISH: 'onPlayerFinish', // When any player finishes (plays last cards)

  // Game triggers
  ON_GAME_START: 'onGameStart',   // At game start (after shop)
  ON_GAME_END: 'onGameEnd',       // At game end

  // Card modification (passive)
  MODIFY_CARD: 'modifyCard',      // Modify card values
  MODIFY_PLAY: 'modifyPlay',      // Modify play validation
  PASSIVE: 'passive',             // Always active

  // Shop triggers
  ON_PURCHASE: 'onPurchase',      // When buying something
  ON_SELL: 'onSell',              // When selling something
  ON_EXCHANGE: 'onExchange',      // When exchanging cards

  // Copy triggers
  ON_NEIGHBOR: 'onNeighbor',               // When a neighboring joker triggers (Mitläufer)

  // Minigame triggers
  ON_TRIGGER_MINIGAME: 'onTriggerMinigame', // When a joker/seal triggers a minigame
};

// =============================================================================
// EFFEKT-TYPEN
// =============================================================================

const EFFECT_TYPE = {
  // Economy
  ADD_COINS: 'addCoins',
  STEAL_COINS: 'stealCoins',
  MULTIPLY_COINS: 'multiplyCoins',

  // Scoring
  ADD_POINTS: 'addPoints',
  ADD_MULT: 'addMult',
  MULTIPLY_MULT: 'multiplyMult',

  // Tempo
  EXTRA_PLAY: 'extraPlay',
  DRAW_CARDS: 'drawCards',
  DISCARD_CARDS: 'discardCards',
  SKIP_TURN: 'skipTurn',

  // Power
  CARD_VALUE_BONUS: 'cardValueBonus',
  SUIT_UPGRADE: 'suitUpgrade',
  WIN_TIES: 'winTies',

  // Defensive
  PREVENT_DRAW: 'preventDraw',
  PROTECT_COINS: 'protectCoins'
};

// =============================================================================
// SPIELZUG-TYPEN
// =============================================================================

const PLAY_TYPE = {
  SINGLE: 'single',
  PAIR: 'pair',
  TRIPLE: 'triple',
  TWO_PAIR: 'twoPairs',
  STRAIGHT: 'straight',
  FLUSH: 'flush',
  FULL_HOUSE: 'fullHouse',
  FOUR_OF_KIND: 'pokerWithKicker',
  STRAIGHT_FLUSH: 'straightFlush',
  ROYAL_FLUSH: 'royalFlush'
};

// Display-Konfiguration für Spielzug-Typen (für UI)
const PLAY_TYPE_DISPLAY = {
  single: { name: 'Einzelkarte', emoji: '🃏', points: 1 },
  pair: { name: 'Pärchen', emoji: '👯', points: 2 },
  triple: { name: 'Drilling', emoji: '🎲', points: 3 },
  twoPairs: { name: 'Zwei Pärchen', emoji: '👯‍♂️', points: 4 },
  straight: { name: 'Straße', emoji: '📈', points: 5 },
  flush: { name: 'Flush', emoji: '🎨', points: 6 },
  fullHouse: { name: 'Full House', emoji: '🏠', points: 7 },
  pokerWithKicker: { name: 'Vierling', emoji: '🎰', points: 8 },
  straightFlush: { name: 'Straight Flush', emoji: '🔥', points: 9 },
  royalFlush: { name: 'Randalaise Flush', emoji: '👑', points: 12 }
};

// Helper für Display-Info
function getPlayTypeDisplay(playType) {
  return PLAY_TYPE_DISPLAY[playType] || { name: playType, emoji: '🎴', points: 0 };
}

// =============================================================================
// BASIS-PUNKTE (Server-autoritativ)
// =============================================================================

const BASE_POINTS = {
  WIN_TRICK: 0, // Keine Basispunkte für Stich - nur Hand-Typ-Punkte zählen
  // Hand-Typ-Punkte (für Scoring-Breakdown)
  PLAY_SINGLE: 1,
  PLAY_PAIR: 2,
  PLAY_TRIPLE: 3,
  PLAY_TWO_PAIR: 4,
  PLAY_STRAIGHT: 5,
  PLAY_FLUSH: 6,
  PLAY_FULL_HOUSE: 7,
  PLAY_FOUR_OF_KIND: 8,
  PLAY_STRAIGHT_FLUSH: 9,
  PLAY_ROYAL_FLUSH: 12
};

// Platzierungs-Bonus basierend auf Spieleranzahl
const PLACEMENT_BONUS = {
  2: [4, 0],           // 2 Spieler: 1. +4, 2. 0
  3: [5, 2, 0],        // 3 Spieler: 1. +5, 2. +2, 3. 0
  4: [5, 3, 1, 0],     // 4 Spieler: 1. +5, 2. +3, 3. +1, 4. 0
  5: [6, 4, 2, 0, 0],  // 5 Spieler: 1. +6, 2. +4, 3. +2, 4.-5. 0
  6: [6, 4, 2, 0, 0, 0] // 6 Spieler: 1. +6, 2. +4, 3. +2, 4.-6. 0
};

// =============================================================================
// STARTWERTE
// =============================================================================

const STARTING_VALUES = {
  COINS: 4,       // Server-autoritativ
  MULT: 1,
  POINTS: 0,
  MAX_JOKERS: 3,
  MAX_SEALS: 3,
  MAX_STASH: 5
};

// =============================================================================
// SPIELPHASEN
// =============================================================================

const GAME_PHASE = {
  LOBBY: 'lobby',
  PRE_GAME_SHOP: 'preGameShop',
  PLAYING: 'playing',
  SHOP: 'shop',
  EXCHANGING: 'exchanging',
  GAME_OVER: 'gameOver'
};

// =============================================================================
// RAUM-LIMITS
// =============================================================================

const MAX_ROOM_MEMBERS = 6;

// =============================================================================
// TRICK HIGHSCORE
// =============================================================================

const DEFAULT_TRICK_HIGHSCORE_STARS = 5; // Startwert: braucht mindestens eine Straße oder Trick mit Boni
const TRICK_HIGHSCORE_BONUS = 2;         // Bonus-Sterne bei Highscore

// =============================================================================
// NICKNAME-VALIDIERUNG
// =============================================================================

const NICKNAME_MIN_LENGTH = 3;
const NICKNAME_MAX_LENGTH = 12;

// =============================================================================
// SPIELMODI (für Private Rooms und Quick Play)
// =============================================================================

const GAME_MODES = [
  {
    value: 'firstToPoints',
    label: 'Wer zuerst kommt',
    icon: '🏆',
    description: 'Wer zuerst X Punkte erreicht, gewinnt',
    defaultTarget: 75,
    minTarget: 1,
    maxTarget: 9999,
    presets: [75, 150, 300],
    unit: 'Punkte',
    unitIcon: '⭐',
  },
  {
    value: 'fixedRounds',
    label: 'Wer zuletzt lacht',
    icon: '📊',
    description: 'Nach X Partien gewinnt wer die meisten Punkte hat',
    defaultTarget: 5,
    minTarget: 1,
    maxTarget: 99,
    presets: [3, 5, 10],
    unit: 'Partien',
    unitIcon: '📊',
  },
];

// =============================================================================
// KARTEN-KONSTANTEN
// =============================================================================

const SUITS = {
  DIAMONDS: { id: 0, symbol: '♦', name: 'Karo', color: 'red' },
  HEARTS: { id: 1, symbol: '♥', name: 'Herz', color: 'red' },
  SPADES: { id: 2, symbol: '♠', name: 'Pik', color: 'black' },
  CLUBS: { id: 3, symbol: '♣', name: 'Kreuz', color: 'black' }
};

const RANKS = ['3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A', '2'];

const RANK_VALUES = {
  '3': 0, '4': 1, '5': 2, '6': 3, '7': 4, '8': 5,
  '9': 6, '10': 7, 'J': 8, 'Q': 9, 'K': 10, 'A': 11, '2': 12
};

// =============================================================================
// POSITIONS-LABELS (für Rundenende-Anzeige)
// =============================================================================

/**
 * Gibt das Label für eine Platzierung zurück.
 * @param {number} index - Position im Ranking (0-basiert)
 * @param {number} total - Gesamtzahl der Spieler
 * @returns {{ title: string, emoji: string, class: string }}
 */
function getPositionLabel(index, total) {
  if (index === 0) return { title: 'Präsident', emoji: '👑', class: 'president' };
  if (index === 1 && total >= 4) return { title: 'Vize-Präsident', emoji: '🥈', class: 'vice-president' };
  if (index === total - 1) return { title: 'Arsch', emoji: '💩', class: 'loser' };
  if (index === total - 2 && total >= 4) return { title: 'Vize-Arsch', emoji: '🥉', class: 'vice-loser' };
  return { title: 'Bürger', emoji: '👤', class: 'citizen' };
}

// =============================================================================
// BADGE TRIGGERS (Auslösezeitpunkte für Abzeichen)
// =============================================================================

const BADGE_TRIGGER = {
  IMMEDIATE: 'immediate',  // Sofort bei Event (z.B. first_trick)
  GAME_END: 'game_end',    // Am Spielende geprüft
};

// =============================================================================
// CARD ANIMATION REASONS (für cardAnimation Events)
// =============================================================================

const CARD_ANIMATION_REASON = {
  JOKER: 'joker',
  PLAY: 'play',
  DRAW: 'draw',
};

// =============================================================================
// SPIELNACHRICHTEN (zentralisierte Fehlermeldungen)
// =============================================================================

const GAME_MESSAGES = {
  // Must play messages
  MUST_PLAY_FIRST_TURN: 'Erster Zug - du musst spielen!',
  MUST_PLAY_ROUND_START: 'Du startest die Runde - du musst spielen!',
  // Shield/Trap messages
  FORCED_PASS_TRAP: '🪤 Stolperfalle! Du musst passen!',
  NO_SHIELD_ACTIVE: 'Kein Schild aktiv!',
  // Permission/Turn messages
  HOST_ONLY_START: 'Nur der Host kann das Spiel starten',
  NOT_YOUR_TURN: 'Nicht dein Zug!',
  ALREADY_FINISHED: 'Du bist bereits ausgeschieden!',
  // Validation messages
  NO_CARDS_SELECTED: 'Keine Karten ausgewählt!',
  CARDS_NOT_IN_HAND: 'Du hast diese Karten nicht!',
  INVALID_PLAY: 'Spielzug nicht gültig!',
  MUST_PLAY_LOWEST: 'Erste Karte muss niedrigste Karte enthalten!',
  LAME_DUCK_NO_FIVE_CARD: 'Lahme Ente kann nicht in 5-Card-Tricks verwendet werden!',
  DRAW_NOT_POSSIBLE: 'Ziehen nicht möglich!',
};

/**
 * Gibt die korrekte "must play" Fehlermeldung zurück
 * @param {boolean} isFirstTurn - Ob es der erste Zug ist
 * @returns {string} Die Fehlermeldung
 */
function getMustPlayMessage(isFirstTurn) {
  return isFirstTurn ? GAME_MESSAGES.MUST_PLAY_FIRST_TURN : GAME_MESSAGES.MUST_PLAY_ROUND_START;
}

// =============================================================================
// EXPORTS
// =============================================================================

module.exports = {
  // Raritäten
  RARITY,

  // Effekte
  EFFECT_CATEGORY,
  TRIGGER,
  EFFECT_TYPE,

  // Badge Trigger
  BADGE_TRIGGER,

  // Spielzüge
  PLAY_TYPE,
  PLAY_TYPE_DISPLAY,
  getPlayTypeDisplay,

  // Punkte
  BASE_POINTS,
  PLACEMENT_BONUS,

  // Startwerte
  STARTING_VALUES,

  // Spielphasen
  GAME_PHASE,

  // Spielmodi
  GAME_MODES,

  // Raum-Limits
  MAX_ROOM_MEMBERS,

  // Trick Highscore
  DEFAULT_TRICK_HIGHSCORE_STARS,
  TRICK_HIGHSCORE_BONUS,

  // Nickname-Validierung
  NICKNAME_MIN_LENGTH,
  NICKNAME_MAX_LENGTH,

  // Karten
  SUITS,
  RANKS,
  RANK_VALUES,

  // Positions-Labels
  getPositionLabel,

  // Spielnachrichten
  GAME_MESSAGES,
  getMustPlayMessage,

  // Card Animation
  CARD_ANIMATION_REASON,
};
